import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { saveVideoFile, getVideoFile, deleteVideoFile } from '../utils/videoStorage';

const ProjectContext = createContext();
const AGENT = 'http://127.0.0.1:8001';

const initialState = {
  projects: [],
  currentProject: null,
  loading: false,
  error: null
};

const projectReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_PROJECTS':
      return { ...state, projects: action.payload, loading: false };
    case 'ADD_PROJECT':
      return { ...state, projects: [...state.projects, action.payload], loading: false };
    case 'UPDATE_PROJECT':
      return {
        ...state,
        projects: state.projects.map(p => p.id === action.payload.id ? action.payload : p),
        currentProject: state.currentProject?.id === action.payload.id ? action.payload : state.currentProject
      };
    case 'DELETE_PROJECT':
      return {
        ...state,
        projects: state.projects.filter(p => p.id !== action.payload),
        currentProject: state.currentProject?.id === action.payload ? null : state.currentProject
      };
    case 'SET_CURRENT_PROJECT':
      return { ...state, currentProject: action.payload };
    default:
      return state;
  }
};

// ─── Helper: check if Local Agent is running ─────────────────────────────────
const isAgentOnline = async () => {
  try {
    const res = await fetch(`${AGENT}/health`, { signal: AbortSignal.timeout(2000) });
    return res.ok;
  } catch {
    return false;
  }
};

// ─── Helper: convert absolute path → relative path for /files/ serving ───────
const toFileUrl = (absolutePath, baseDir) => {
  if (!absolutePath) return null;
  if (absolutePath.startsWith('http://') || absolutePath.startsWith('https://') || absolutePath.startsWith('blob:')) {
    return absolutePath;
  }
  
  // Normalize all backslashes to forward slashes
  const fwd = absolutePath.replace(/\\/g, '/');
  const base = (baseDir || '').replace(/\\/g, '/').replace(/\/$/, '');
  
  if (base && fwd.toLowerCase().startsWith(base.toLowerCase())) {
    const rel = fwd.slice(base.length).replace(/^\//, '');
    return `${AGENT}/files/${rel.split('/').map(seg => encodeURIComponent(seg)).join('/')}`;
  }
  
  // Fallback for any absolute system path (macOS/Windows)
  return `${AGENT}/stream-video?path=${encodeURIComponent(absolutePath)}`;
};

// ─── Helper: upload File object via XHR with progress callback ────────────────
const uploadFileWithProgress = (file, projectName, onProgress) =>
  new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append('project_name', projectName);
    fd.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${AGENT}/projects/upload-video`, true);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch { reject(new Error('Invalid response from agent')); }
      } else {
        reject(new Error(`Upload failed: HTTP ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.ontimeout = () => reject(new Error('Upload timed out'));
    xhr.send(fd);
  });

// ─── Default project settings template ───────────────────────────────────────
const defaultSettings = () => ({
  format: '16:9',
  resolution: '1080p',
  subtitle: {
    enabled: true,
    maxWordsPerLine: 8,
    fontSize: 36,
    fontColor: '#ffffff',
    backgroundColor: '#000000',
    backgroundOpacity: 70,
    position: 'bottom-center',
    customPosition: { enabled: false, x: 50, y: 90 },
    fontFamily: 'Arial',
    customFont: null,
    textShadow: { enabled: true, color: '#000000', blur: 2, offsetX: 1, offsetY: 1 }
  },
  subtitleStyle: { font: 'Arial', size: 24, color: '#ffffff', backgroundColor: '#000000', maxWordsPerLine: 8 },
  watermark: { enabled: false, path: null, position: 'bottom-right', opacity: 0.8, size: 'medium' }
});

// ─── Provider ─────────────────────────────────────────────────────────────────
export const ProjectProvider = ({ children }) => {
  const [state, dispatch] = useReducer(projectReducer, initialState);

  useEffect(() => { loadProjects(); }, []);

  // LocalStorage fallback persistence
  // We keep videoPath and videoFile (including url)
  useEffect(() => {
    if (state.projects.length > 0) {
      localStorage.setItem('beevids-projects', JSON.stringify(state.projects));
    }
  }, [state.projects]);

  // ─── loadProjects ─────────────────────────────────────────────────────────
  const loadProjects = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      console.log('🔄 Loading projects...');

      const online = await isAgentOnline();
      console.log('🔌 Local Agent Status:', online ? 'ONLINE' : 'OFFLINE');

      if (online) {
        // Load from Local Agent
        const [listRes, dirRes] = await Promise.all([
          fetch(`${AGENT}/projects/list`),
          fetch(`${AGENT}/projects/directory`)
        ]);

        const projects = await listRes.json();
        const { base: baseDir } = await dirRes.json();
        console.log('📂 Projects loaded from Agent:', projects.length, 'Base directory:', baseDir);

        const withUrls = projects.map(p => {
          const url = p.videoPath ? toFileUrl(p.videoPath, baseDir) : null;
          return {
            ...p,
            videoFile: p.videoPath
              ? { ...(p.videoFile || {}), url: url }
              : p.videoFile
          };
        });

        dispatch({ type: 'SET_PROJECTS', payload: withUrls });
        return;
      }

      // Offline fallback: LocalStorage + IndexedDB
      console.log('📦 Loading from LocalStorage (Offline Fallback)');
      const saved = JSON.parse(localStorage.getItem('beevids-projects') || '[]');
      const withVideos = await Promise.all(
        saved.map(async (p) => {
          if (!p.videoFile) return p;
          
          // If we already have a valid agent URL, keep it (it might still work if agent was just briefly offline)
          if (p.videoFile.url && p.videoFile.url.startsWith('http')) {
            return p;
          }

          const idb = await getVideoFile(p.id);
          return idb?.url ? { ...p, videoFile: idb } : p;
        })
      );
      dispatch({ type: 'SET_PROJECTS', payload: withVideos });

    } catch (error) {
      console.error('❌ Error loading projects:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  // ─── createProject ────────────────────────────────────────────────────────
  const createProject = async (videoInput, projectName, onProgress) => {
    dispatch({ type: 'SET_LOADING', payload: true });

    // Normalise input
    let fileObject = null;   // actual File object
    let localPath  = null;   // absolute Windows path (from /select-file)
    let previewUrl = null;   // blob URL for immediate display

    if (typeof videoInput === 'string') {
      // came from /select-file → absolute path
      localPath = videoInput;
    } else if (videoInput instanceof File) {
      fileObject = videoInput;
      previewUrl = URL.createObjectURL(videoInput);
    } else if (videoInput?.file instanceof File) {
      fileObject = videoInput.file;
      previewUrl = videoInput.url || URL.createObjectURL(fileObject);
    }

    const project = {
      id: uuidv4(),
      name: projectName || `Project_${Date.now()}`,
      videoPath: localPath || (fileObject?.name ?? null),
      videoFile: fileObject
        ? { name: fileObject.name, size: fileObject.size, type: fileObject.type, url: previewUrl }
        : null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      duration: 0,
      status: 'video-only',
      transcript: null,
      translation: null,
      parts: [],
      groups: [],
      settings: defaultSettings()
    };

    try {
      const online = await isAgentOnline();

      if (online) {
        const dirRes  = await fetch(`${AGENT}/projects/directory`);
        const { path: projectsDir, base: baseDir } = await dirRes.json();
        const projDir = `${projectsDir}/${project.name}`;

        if (localPath) {
          // ── Case A: file already on disk (picker via agent) ────────────
          const fname           = localPath.split(/[\\/]/).pop();
          const internalPath    = `${projDir}/storage/${fname}`;

          const copyRes = await fetch(`${AGENT}/projects/copy-file`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ source: localPath, dest: internalPath })
          });
          const copyData = await copyRes.json();

          if (copyData.success) {
            project.videoPath = internalPath;
            project.videoFile = { name: fname, url: toFileUrl(internalPath, baseDir) };
          }
          if (onProgress) onProgress(100);

        } else if (fileObject) {
          // ── Case B: browser file upload ────────────────────────────────
          if (onProgress) onProgress(1); // signal start immediately
          const uploadData = await uploadFileWithProgress(fileObject, project.name, onProgress);

          if (uploadData.success) {
            project.videoPath = uploadData.path;
            project.videoFile = {
              name: fileObject.name,
              size: fileObject.size,
              type: fileObject.type,
              url: toFileUrl(uploadData.path, baseDir)
            };
          }
        }

        // Save project.json
        await fetch(`${AGENT}/projects/save`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(project)
        });

        project.path = projDir;

      } else {
        // ── Offline fallback: IndexedDB ────────────────────────────────────
        if (fileObject) {
          if (onProgress) onProgress(50);
          const idbData = await saveVideoFile(project.id, fileObject);
          project.videoFile = idbData;
          if (onProgress) onProgress(100);
        }
      }
    } catch (err) {
      console.error('❌ createProject error:', err);
      // Still add the project with whatever we have
    }

    dispatch({ type: 'ADD_PROJECT', payload: project });
    return project;
  };

  // ─── updateProject ────────────────────────────────────────────────────────
  const updateProject = async (projectId, updates) => {
    const project = state.projects.find(p => p.id === projectId);
    if (!project) throw new Error('Project not found');

    const updated = { ...project, ...updates, updatedAt: new Date().toISOString() };

    try {
      await fetch(`${AGENT}/projects/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated)
      });
    } catch {
      const all = state.projects.map(p => p.id === projectId ? updated : p);
      localStorage.setItem('beevids-projects', JSON.stringify(all));
    }

    dispatch({ type: 'UPDATE_PROJECT', payload: updated });
    return updated;
  };

  // ─── deleteProject ────────────────────────────────────────────────────────
  const deleteProject = async (projectId) => {
    const project = state.projects.find(p => p.id === projectId);
    if (!project) throw new Error('Project not found');

    try {
      await fetch(`${AGENT}/projects/${encodeURIComponent(project.name)}`, { method: 'DELETE' });
    } catch {
      const all = state.projects.filter(p => p.id !== projectId);
      localStorage.setItem('beevids-projects', JSON.stringify(all));
      await deleteVideoFile(projectId);
    }

    dispatch({ type: 'DELETE_PROJECT', payload: projectId });
  };

  const setCurrentProject = (project) => {
    dispatch({ type: 'SET_CURRENT_PROJECT', payload: project });
  };

  const value = { ...state, loadProjects, createProject, updateProject, deleteProject, setCurrentProject };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) throw new Error('useProject must be used within a ProjectProvider');
  return context;
};